import type { ResourceType } from '@/services/api/api.types'
import { resolvePrimaryAccess } from '@/utils/contentAccess'

/**
 * The one mode whose availability depends on the primary file, and the one to fall back
 * to when it isn't available. Both are style `name`s from CMDB.
 *
 * `media-mode` mirrors the backend's `media_mode_presentation_style_name` config, which is
 * what `_apply_presentation_style` matches on to set `is_media_mode` — a rename there needs
 * one here. Compared case-insensitively, as the backend does.
 */
export const MEDIA_MODE = 'media-mode'
export const FALLBACK_MODE = 'discovery'

/**
 * The value standing in for prose the expert wrote before this picker existed —
 * decision 16. Never a real style name, so it can't collide with one.
 */
export const CUSTOM_DELIVERY_MODE = '__custom__'

export const MEDIA_BARRED_TOOLTIP = 'Your Primary File must be a media file or Full Access to select media.'

type PrimaryFacts = {
  type: ResourceType | null | undefined
  showInAppSupported: boolean | null | undefined
  /**
   * The file's stored `show_in_app`. Pass it for a primary already leading the module, where
   * a deliberate hide bars Media; leave it out to ask what the *format* allows, which is
   * what the swap picker needs — the swap corrects the access, so a candidate is judged by
   * what it will be once it leads, not by what it is now.
   */
  showInApp?: boolean | null
  /** Whether the file failed to process. The prototype's `mediaEligible` rules one out. */
  hasError?: boolean | null
}

export const isMediaModeName = (name: string | null | undefined): boolean => name?.toLowerCase() === MEDIA_MODE

/**
 * Her Step 3 change: "Renames 'Media Mode' to 'Media'". Overridden here rather than in the
 * catalog, which still carries the old `display_name` — renaming at source is a CMDB change
 * and would move the label for every other consumer at the same time.
 *
 * Lives with the mode rather than with the picker so the read-only view says the same word
 * the expert chose, instead of the raw style name.
 */
const MODE_LABELS: Record<string, string> = {
  [MEDIA_MODE]: 'Media',
}

export const deliveryModeLabel = (name: string, fallback: string): string =>
  MODE_LABELS[name.trim().toLowerCase()] ?? fallback

/**
 * What each mode means, in the expert's terms. The catalog's `description` is the prompt
 * template the Sidekick is given — accurate, and not something to show anyone — so the
 * copy here comes from Courtney's mockup, keyed by the style's name.
 *
 * A style with no entry has no blurb rather than a fallback: printing the template is
 * worse than printing nothing.
 */
const MODE_BLURBS: Record<string, string> = {
  [MEDIA_MODE]:
    'Users experience your primary media file first, with the Sidekick helping navigate and answer questions.',
  discovery: 'Question-driven exploration; the Sidekick guides users through your content with open-ended questions.',
  lecture: 'Structured, linear walkthrough; the Sidekick presents your content topic by topic.',
}

export const deliveryModeBlurb = (name: string): string | undefined => MODE_BLURBS[name.trim().toLowerCase()]

/**
 * Decision 5, Courtney's rule: "Media will be not selectable if the primary file is txt,
 * xlsx, csv, md, or manually entered". Read off the format, through `resolvePrimaryAccess`
 * with no stored flag, rather than a second list of types to keep in step.
 *
 * Her rule reads on access, not only on format: "Your Primary File must be a media file or
 * Full Access to select media". So a caller that knows the file's stored `show_in_app`
 * passes it, and a lead file the expert hid bars Media — the module demotes and says so,
 * rather than the file being silently un-hidden to keep the mode (decision 27).
 *
 * A caller that passes no flag is asking what the format allows. That is the swap picker,
 * which judges a candidate by what it will be once it leads: the swap resolves its access
 * in the same write, so a hidden candidate is not barred, it is corrected.
 */
export function canSelectMediaMode(primary: PrimaryFacts): boolean {
  // A file that failed to process cannot lead a Media module whatever its format says.
  // The approved prototype spells this out: eligibility "does not wait for the transcode
  // — only a failed file is ruled out", and its `reconcileMode` demotes on the same test.
  if (primary.hasError) return false
  // An unknown type is not a media file. `resolvePrimaryAccess` reads it as displayable,
  // which is right for "what access does this get" and wrong for "may they pick Media" —
  // callers that ask before the primary lands would otherwise get a yes.
  if (!primary.type) return false
  return resolvePrimaryAccess(primary) === 'full_access'
}

/**
 * "Media is selected by default. If disallowed based on file type, then choose Discovery
 * by default." Falls back to any non-media mode if the catalog has no Discovery, so a
 * renamed or removed style can't leave a new module with no default at all.
 *
 * Returns undefined when there is nothing to pick, which is the caller's cue to leave the
 * form alone rather than write a guess into it.
 */
export function defaultDeliveryModeName(primary: PrimaryFacts, styleNames: string[]): string | undefined {
  const find = (predicate: (name: string) => boolean) => styleNames.find(name => name && predicate(name.toLowerCase()))

  if (canSelectMediaMode(primary)) {
    const media = find(isMediaModeName)
    if (media) return media
  }

  return find(name => name === FALLBACK_MODE) ?? find(name => name !== MEDIA_MODE)
}

/**
 * Which radio a saved module starts on.
 *
 * C2-2598 added the recorded choice and deliberately did not backfill it, so every module
 * saved before it has none. Daniel's integration guide for that ticket asks the client to
 * fall back to the stored `is_media_mode` for those, because it is the only signal they
 * carry. Without the fallback a legacy media module reads as Custom, and the automatic
 * change to Discovery is guarded on the selected mode, so it never runs for one.
 *
 * Prose with no choice and no flag is decision 16's Custom case.
 */
export function storedDeliveryMode(saved: {
  presentationStyleName?: string | null
  presentationStyle?: string | null
  isMediaMode?: boolean | null
}): string {
  const chosen = saved.presentationStyleName?.trim()
  if (chosen) return chosen
  if (saved.isMediaMode) return MEDIA_MODE
  return saved.presentationStyle?.trim() ? CUSTOM_DELIVERY_MODE : ''
}

/**
 * The mode a primary forces the module onto, or undefined when it forces nothing.
 *
 * Media is the only mode a primary can bar, so this is the whole rule. Shared by the
 * picker, which applies it when the primary changes underneath, and by the swap, which
 * has to save it: the swap itself persists immediately, so a correction left on the form
 * leaves the module stored as Media with a primary that cannot carry it.
 */
export function demotionForPrimary(
  currentMode: string,
  primary: PrimaryFacts,
  styleNames: string[],
): string | undefined {
  if (!isMediaModeName(currentMode)) return undefined
  if (canSelectMediaMode(primary)) return undefined

  const fallback = defaultDeliveryModeName(primary, styleNames)
  return fallback && !isMediaModeName(fallback) ? fallback : undefined
}
